// import {createColumnHelper, useReactTable, type ColumnDef} from "@tanstack/react-table";
import {useMediaQuery} from "@mantine/hooks";
import {lazy, useState, type ReactNode} from "react";
import {CalendarView} from "~/components/ottopilotDataViewer/calendarView";
import {twJoin} from "tailwind-merge";
import {CalendarToolbarActions} from "~/components/beatPlanningCalendar/calendarToolbarActions";
import {CardsView} from "~/components/ottopilotDataViewer/cardsView";
import {ConfigureColumnsComponent} from "~/components/ottopilotDataViewer/configureColumnsComponent";
import {DataDebuggingPanel} from "~/components/ottopilotDataViewer/dataDebuggingPanel";
import {DataViewerDateRangeFilter} from "~/components/ottopilotDataViewer/dataViewerDateRangeFilter";
import {
    FilterAddFilterButton,
    FilterClearAllButton,
    FilterVisibleLineItems,
} from "~/components/ottopilotDataViewer/filterComponentStupider";
import {GraphView} from "~/components/ottopilotDataViewer/graphView";
import {KanbanBoardView} from "~/components/ottopilotDataViewer/kanbanBoardView";
import {ListView} from "~/components/ottopilotDataViewer/listView";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {RowEngagementPanel} from "~/components/ottopilotDataViewer/rowEngagementPanel";
import {JourneyScopeFilterComponent} from "~/components/ottopilotDataViewer/journeyScopeFilterComponent";
import {CalendarCustomizeViewTrigger} from "~/components/ottopilotDataViewer/calendarCustomizeViewTrigger";
import {ScopeFilterComponent} from "~/components/ottopilotDataViewer/scopeFilterComponent";
import {SearchComponent} from "~/components/ottopilotDataViewer/searchComponent";
import {SortComponent} from "~/components/ottopilotDataViewer/sortComponent";
import {TableErrorComponent} from "~/components/ottopilotDataViewer/tableErrorComponent";
import {TableView} from "~/components/ottopilotDataViewer/tableView";
import {ViewType} from "~/components/ottopilotDataViewer/view";
import {MobileDataViewerFilterDrawer} from "~/components/ottopilotDataViewer/mobileDataViewerFilterDrawer";
import {MobileDataViewerOverflowMenu} from "~/components/ottopilotDataViewer/mobileDataViewerOverflowMenu";
import {ViewPicker} from "~/components/ottopilotDataViewer/viewPicker";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {VerticalSpacer} from "@vesta/global-common-typescript/components/verticalSpacer";
import {GenericTableView} from "./genericTableView";

const LazyCalendarView = lazy(async () => {
    const module = await import("~/components/ottopilotDataViewer/calendarView");
    return {default: module.CalendarView};
});

const MOBILE_TOOLBAR_BREAKPOINT = "(max-width: 47.9375em)";

/** Horizontally scrollable filter pills; Add Filter / Clear sit in a separate column at the row end. */
const FILTER_ROW_SCROLL_CLASS =
    "relative z-[2] min-w-0 flex-1 overflow-x-auto overflow-y-visible overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";
const FILTER_ROW_TRACK_CLASS =
    "flex w-max flex-nowrap items-center gap-x-ds-space-100 [&>*]:shrink-0";
const FILTER_ROW_ACTIONS_CLASS =
    "flex shrink-0 flex-nowrap items-center gap-x-ds-space-100 self-stretch";

const CALENDAR_DESKTOP_TOOLBAR_SCROLL_TRACK_CLASS =
    twJoin(FILTER_ROW_TRACK_CLASS, "[&_.beat-planning-calendar__toolbar-right]:!flex-nowrap");

/** Desktop calendar toolbar sits in the right column; content aligns end and scrolls when it overflows. */
const CALENDAR_DESKTOP_TOOLBAR_RIGHT_SCROLL_CLASS =
    "relative z-[2] flex min-w-0 max-w-full justify-end overflow-x-auto overflow-y-visible overscroll-x-contain [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";

/** Sort / scope / view-switcher cluster: scroll horizontally when the content column is narrow.
 * Padding keeps the sort count badge (`-top-2 -right-2`) inside the scrollport — browsers treat
 * `overflow-x: auto` as also clipping the Y axis even when `overflow-y: visible` is set.
 */
const DESKTOP_TOOLBAR_RIGHT_SCROLL_CLASS =
    "relative z-[2] min-w-0 max-w-full overflow-x-auto overscroll-x-contain pt-ds-space-150 pr-ds-space-100 -mt-ds-space-150 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden";
const DESKTOP_TOOLBAR_RIGHT_TRACK_CLASS =
    "flex w-max flex-nowrap items-center gap-x-ds-space-100 [&>*]:shrink-0";

const CONTENT_HORIZONTAL_INSET_CLASS = "px-ds-space-200 sm:px-ds-space-300";

/** Matches table page footer gap ({@link GenericTableView}). */
const CONTENT_BOTTOM_GAP_CLASS = "h-ds-space-250 w-full min-w-0 shrink-0";

function viewUsesContentHorizontalInset(viewType: ViewType | undefined): boolean {
    if (viewType === undefined) {
        return false;
    }
    return (
        viewType === ViewType.kanbanBoard ||
        viewType === ViewType.calendar ||
        viewType === ViewType.cards ||
        viewType === ViewType.graph
    );
}

function viewUsesBottomContentGap(viewType: ViewType | undefined): boolean {
    if (viewType === undefined) {
        return false;
    }
    return viewType === ViewType.kanbanBoard || viewType === ViewType.calendar;
}

// TODO: Figure out where I want to manage:
//     1. loading state
//     2. error state
//     3. data not found state
//     4. healthy state
// possible options are once in OttopilotDataViewer, or per-view
export function OttopilotDataViewer<
    T extends {
        id: Uuid;
    },
>({
    controller,
    className,
    stickyToolbar = false,
    hideViewPicker = false,
    hideDateRangeFilter = false,
    hideAddFilterButton = false,
    hidePageFooter = false,
    hideConfigureColumns = false,
    hideMobileOverflowMenu = false,
    horizontalInsetClass,
    toolbarTrailingContent,
    filterLeadingContent,
}: {
    controller: OttopilotDataViewerController<T>;
    className?: string;
    /** When true, search/filters/sort stay visible while the view body scrolls. */
    stickyToolbar?: boolean;
    /** Hides view tabs when the page has a single fixed view (e.g. disposition kanban). */
    hideViewPicker?: boolean;
    /** Hides the created-at date range pill in the filter row. */
    hideDateRangeFilter?: boolean;
    /** Hides the "+ Add Filter" control in the filter row (e.g. journey profile Events). */
    hideAddFilterButton?: boolean;
    /**
     * Hides the paginated table/list page footer. Use on entity profile where the
     * page owns a fixed action footer that must align with the sidebar separator.
     */
    hidePageFooter?: boolean;
    /** Hides the Columns control (e.g. journey profile list where Figma shows Display by instead). */
    hideConfigureColumns?: boolean;
    /** Hides the mobile ⋯ overflow menu (e.g. journey profile where view pill is shown inline). */
    hideMobileOverflowMenu?: boolean;
    /** Overrides default toolbar/content horizontal inset (e.g. entity profile container padding). */
    horizontalInsetClass?: string;
    /** Extra controls after Sort / scope / Columns (e.g. Disposition L1). */
    toolbarTrailingContent?: ReactNode;
    /** Extra filter pills after Date Range and before visible column filters (e.g. customer profile Lifecycle). */
    filterLeadingContent?: ReactNode;
}) {
    const isMobileToolbar = useMediaQuery(MOBILE_TOOLBAR_BREAKPOINT, false, {getInitialValueInEffect: true});
    const [mobileSearchExpanded, setMobileSearchExpanded] = useState(false);
    const resolvedHorizontalInsetClass = horizontalInsetClass ?? CONTENT_HORIZONTAL_INSET_CLASS;
    const hasSearch = controller.searchConfiguration.searchColumns.length > 0;
    const hasScopeFilter =
        controller.scopeFilterConfig !== undefined && controller.scopeFilterConfig.options.length > 0;
    const hasJourneyScopeFilter = controller.journeyScopeFilterConfig !== undefined;
    const hasCalendarCustomizeView = controller.calendarDisplayMetrics !== undefined;

    const currentView =
        controller.currentViewId === null
            ? null
            : controller.views.find((view) => view.id === controller.currentViewId);
    const isCalendarView = currentView?.viewType === ViewType.calendar;
    const shouldHideDateRangeFilter = hideDateRangeFilter === true || isCalendarView === true;
    const isFocusView = currentView?.viewType === ViewType.focus;
    const usesContentHorizontalInset = viewUsesContentHorizontalInset(currentView?.viewType);
    const usesBottomContentGap = viewUsesBottomContentGap(currentView?.viewType);
    const isDesktopCalendarToolbar = isCalendarView === true && isMobileToolbar === false;

    const calendarToolbarTrailingContent =
        isDesktopCalendarToolbar === true ? (
            <>
                {hasScopeFilter === true && controller.scopeFilterConfig !== undefined ? (
                    <ScopeFilterComponent
                        controller={controller}
                        scopeFilterConfig={controller.scopeFilterConfig}
                    />
                ) : null}
                {hasJourneyScopeFilter === true && controller.journeyScopeFilterConfig !== undefined ? (
                    <JourneyScopeFilterComponent
                        controller={controller}
                        journeyScopeFilterConfig={controller.journeyScopeFilterConfig}
                        disabled={controller.journeyScopeFilterDisabled === true}
                    />
                ) : null}
            </>
        ) : undefined;

    return (
        <div
            className={twJoin(
                // gap-y-ds-space-200 sm:gap-y-ds-space-300
                "relative min-h-0 h-full w-full min-w-0 grid grid-cols-1 grid-rows-[auto_auto_auto_minmax(0,1fr)_auto_auto]",
                className,
            )}
        >
            <div
                className={twJoin(
                    "w-full min-w-0",
                    resolvedHorizontalInsetClass,
                    // hideViewPicker normally uses pt-0; sticky toolbars need room for the sort/filter count badge (-top-2).
                    stickyToolbar === true && hideViewPicker === true
                        ? "pt-ds-space-150"
                        : hideViewPicker === true
                          ? "pt-0"
                          : "pt-ds-space-200",
                    stickyToolbar === true &&
                        "sticky top-0 z-20 overflow-visible bg-ds-elevation-surface-current shadow-[0_0.25rem_0.5rem_-0.25rem_rgba(9,30,66,0.08)]",
                )}
            >
                {hideViewPicker === false ? (
                    <ViewPicker
                        controller={controller}
                        className={twJoin(
                            isFocusView === true ? "mb-0" : "mb-ds-space-200",
                            isMobileToolbar === true && "hidden",
                        )}
                    />
                ) : null}
                {isFocusView === false ? (
                    <>
                {mobileSearchExpanded === false && isDesktopCalendarToolbar === true ? (
                    <>
                        <div className="flex w-full min-w-0 flex-wrap items-center gap-x-ds-space-100 gap-y-ds-space-100">
                            {hasSearch === true ? (
                                <div className="w-full min-w-0 max-w-full shrink-0 sm:w-auto sm:max-w-[20rem] sm:flex-none">
                                    <SearchComponent
                                        controller={controller}
                                        placeholder={controller.searchPlaceholder}
                                        onMobileSearchExpandedChange={setMobileSearchExpanded}
                                    />
                                </div>
                            ) : null}
                            <div className="ml-auto flex min-w-0 max-w-full flex-1 basis-[min(100%,28rem)] items-center justify-end gap-x-ds-space-100">
                                <div className={CALENDAR_DESKTOP_TOOLBAR_RIGHT_SCROLL_CLASS}>
                                    <div className={CALENDAR_DESKTOP_TOOLBAR_SCROLL_TRACK_CLASS}>
                                        <CalendarToolbarActions
                                            isMobile={isMobileToolbar}
                                            trailingContent={calendarToolbarTrailingContent}
                                        />
                                    </div>
                                </div>
                                {hasCalendarCustomizeView === true ? (
                                    <CalendarCustomizeViewTrigger controller={controller} />
                                ) : null}
                            </div>
                        </div>
                        <div className="flex w-full min-w-0 items-center gap-x-ds-space-100 py-ds-space-100">
                            <div className={FILTER_ROW_SCROLL_CLASS}>
                                <div className={FILTER_ROW_TRACK_CLASS}>
                                    {shouldHideDateRangeFilter === false ? (
                                        <DataViewerDateRangeFilter controller={controller} />
                                    ) : null}
                                    {filterLeadingContent}
                                    <FilterVisibleLineItems controller={controller} />
                                </div>
                            </div>
                            <div className={FILTER_ROW_ACTIONS_CLASS}>
                                {hideAddFilterButton !== true ? (
                                    <FilterAddFilterButton controller={controller} />
                                ) : null}
                                <FilterClearAllButton controller={controller} />
                            </div>
                        </div>
                    </>
                ) : (
                    <>
                <div
                    className={twJoin(
                        "w-full min-w-0",
                        mobileSearchExpanded === false && "flex min-w-0 items-center gap-x-ds-space-100",
                    )}
                >
                    <div
                        className={twJoin(
                            "grid min-w-0 grid-flow-col gap-ds-space-100",
                            mobileSearchExpanded === false ? "justify-start" : "",
                            // Keep search from eating the whole row so trailing controls can scroll.
                            mobileSearchExpanded === false && isDesktopCalendarToolbar === false
                                ? "min-w-0 max-w-[min(100%,20rem)] shrink"
                                : "",
                        )}
                    >
                        {hasSearch === true ? (
                            <div
                                className={twJoin(
                                    "min-w-0",
                                    isMobileToolbar === true && mobileSearchExpanded === true
                                        ? "w-full flex-[1_1_100%]"
                                        : "w-full max-w-full md:max-w-[20rem] md:w-auto md:flex-none",
                                )}
                            >
                                <SearchComponent
                                    controller={controller}
                                    placeholder={controller.searchPlaceholder}
                                    onMobileSearchExpandedChange={setMobileSearchExpanded}
                                />
                            </div>
                        ) : null}
                    </div>
                    {mobileSearchExpanded === false ? (
                        isDesktopCalendarToolbar === true ? (
                            <div className={FILTER_ROW_SCROLL_CLASS}>
                                <div className={CALENDAR_DESKTOP_TOOLBAR_SCROLL_TRACK_CLASS}>
                                    <CalendarToolbarActions
                                        isMobile={isMobileToolbar}
                                        trailingContent={calendarToolbarTrailingContent}
                                    />
                                </div>
                            </div>
                        ) : (
                        <div className={twJoin(DESKTOP_TOOLBAR_RIGHT_SCROLL_CLASS, "ml-auto flex-1")}>
                            <div className={twJoin(DESKTOP_TOOLBAR_RIGHT_TRACK_CLASS, "ml-auto")}>
                            {isCalendarView === true ? (
                                <CalendarToolbarActions
                                    isMobile={isMobileToolbar}
                                    trailingContent={calendarToolbarTrailingContent}
                                />
                            ) : (
                                <SortComponent controller={controller} />
                            )}
                            {isCalendarView === false &&
                            hasScopeFilter === true &&
                            controller.scopeFilterConfig !== undefined &&
                            isMobileToolbar === false ? (
                                <ScopeFilterComponent
                                    controller={controller}
                                    scopeFilterConfig={controller.scopeFilterConfig}
                                />
                            ) : null}
                            {isCalendarView === false &&
                            hasJourneyScopeFilter === true &&
                            controller.journeyScopeFilterConfig !== undefined &&
                            isMobileToolbar === false ? (
                                <JourneyScopeFilterComponent
                                    controller={controller}
                                    journeyScopeFilterConfig={controller.journeyScopeFilterConfig}
                                    disabled={controller.journeyScopeFilterDisabled === true}
                                />
                            ) : null}
                            {isMobileToolbar === false &&
                            isCalendarView === false &&
                            hideConfigureColumns !== true ? (
                                <ConfigureColumnsComponent controller={controller} />
                            ) : null}
                            {isCalendarView === false && toolbarTrailingContent !== undefined
                                ? toolbarTrailingContent
                                : null}
                            {isMobileToolbar === true ? (
                                <>
                                    <MobileDataViewerFilterDrawer
                                        controller={controller}
                                        isCalendarView={isCalendarView}
                                    />
                                    {hideMobileOverflowMenu !== true ? (
                                        <MobileDataViewerOverflowMenu controller={controller} />
                                    ) : null}
                                </>
                            ) : null}
                            </div>
                        </div>
                        )
                    ) : null}
                </div>
                {mobileSearchExpanded === false && isDesktopCalendarToolbar === false ? (
                    <div className="flex w-full min-w-0 items-center gap-x-ds-space-100 py-ds-space-100">
                        <div className={FILTER_ROW_SCROLL_CLASS}>
                            <div className={FILTER_ROW_TRACK_CLASS}>
                                {shouldHideDateRangeFilter === false ? (
                                    <DataViewerDateRangeFilter controller={controller} />
                                ) : null}
                                {isMobileToolbar === true ? (
                                    <>
                                        {hasScopeFilter === true && controller.scopeFilterConfig !== undefined ? (
                                            <ScopeFilterComponent
                                                controller={controller}
                                                scopeFilterConfig={controller.scopeFilterConfig}
                                            />
                                        ) : null}
                                        {hasJourneyScopeFilter === true &&
                                        controller.journeyScopeFilterConfig !== undefined ? (
                                            <JourneyScopeFilterComponent
                                                controller={controller}
                                                journeyScopeFilterConfig={controller.journeyScopeFilterConfig}
                                                disabled={controller.journeyScopeFilterDisabled === true}
                                            />
                                        ) : null}
                                    </>
                                ) : null}
                                {filterLeadingContent}
                                <FilterVisibleLineItems controller={controller} />
                            </div>
                        </div>
                        <div className={FILTER_ROW_ACTIONS_CLASS}>
                            {hideAddFilterButton !== true ? (
                                <FilterAddFilterButton controller={controller} />
                            ) : null}
                            <FilterClearAllButton controller={controller} />
                        </div>
                    </div>
                ) : null}
                    </>
                )}
                    </>
                ) : null}
            </div>

            <div />

            {/* <div className="w-full py-ds-space-100 flex flex-row gap-x-ds-space-100 gap-y-ds-space-100 items-center [&>*]:flex-none flex-wrap">
                <SortComponent controller={controller} />

                <div className="w-[0.0625rem] h-8 bg-ds-border" />

                {/* <ItemBuilder
                    items={controller.columns.lineItems}
                    itemBuilder={(column) => (
                        <FilterLineItemComponent
                            controller={controller}
                            column={column}
                            key={column.id}
                        />
                    )}
                /> * /}

                {/* <div className="w-[0.0625rem] h-8 bg-ds-border" /> * /}

                {/* <FilterComponent controller={controller} /> * /}
                {/* <FilterComponentStupid controller={controller} /> * /}
                <FilterComponentStupider controller={controller} />

                {/* <Button
                // onClick={() => (window.location.href = `/w/${workspace.id}/customers/create`)}
                >
                >
                    Filter
                </Button> * /}
            </div> */}

            <VerticalSpacer className={isFocusView === true ? "h-0 min-h-0 overflow-hidden" : "h-ds-space-100"} />

            <div
                className={twJoin(
                    "flex min-h-0 h-full min-w-0 w-full flex-col overflow-hidden",
                    usesContentHorizontalInset === true && resolvedHorizontalInsetClass,
                )}
            >
                <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
                    {currentView === null ? (
                    <TableErrorComponent error="No view selected" />
                ) : currentView === undefined ? (
                    <TableErrorComponent error="Invalid view selected" />
                ) : currentView.viewType === ViewType.cards ? (
                    <CardsView
                        controller={controller}
                        view={currentView}
                    />
                ) : currentView.viewType === ViewType.list ? (
                    <div className="w-full h-full relative">
                        <div className="absolute inset-0 overflow-hidden">
                        <ListView
                            controller={controller}
                            view={currentView}
                            hidePageFooter={hidePageFooter}
                            horizontalInsetClass={resolvedHorizontalInsetClass}
                        />
                        </div>
                    </div>
                ) : currentView.viewType === ViewType.graph ? (
                    <GraphView
                        controller={controller}
                        view={currentView}
                    />
                ) : currentView.viewType === ViewType.kanbanBoard ? (
                    <div className="w-full h-full relative">
                        <div className="absolute inset-0 overflow-hidden">
                        <KanbanBoardView<T>
                                controller={controller}
                                columnConfig={currentView.columnConfig}
                                filterFiledId={currentView.filterFieldId}
                                currentView={currentView}
                            />
                        </div>
                    </div>
                ) : currentView.viewType === ViewType.calendar ? (
                    <div className="w-full h-full relative">
                        <div className="absolute inset-0 overflow-hidden">
                        <CalendarView<T>
                                controller={controller}
                                view={currentView}
                            />
                        </div>
                    </div>
                ) : currentView.viewType === ViewType.focus ? (
                    <div className="flex min-h-0 flex-1 w-full flex-col overflow-hidden">
                        {currentView.focusRenderer(controller)}
                    </div>
                ) : // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
                currentView.viewType === ViewType.table ? (
                   <div className="flex min-h-0 h-full min-w-0 w-full h-full relative">
                    <div className="h-full w-full absolute inset-0">
                    <GenericTableView
                        controller={controller}
                        view={currentView}
                        hidePageFooter={hidePageFooter}
                        horizontalInsetClass={resolvedHorizontalInsetClass}
                    />
                    </div>
                   </div>
                ) : (
                        <TableErrorComponent error="Unknown view" />
                    )}
                </div>
                {usesBottomContentGap === true ? (
                    <div
                        className={CONTENT_BOTTOM_GAP_CLASS}
                        aria-hidden
                    />
                ) : null}
            </div>

            <RowEngagementPanel
                controller={controller}
            />

            {controller.listCardChrome}

            <DataDebuggingPanel
                controller={controller}
            />
        </div>
    );
}
